import { useEffect, useMemo } from "react";
import _ from "lodash";

import { InspectedItemDetails, Project, User } from "../utils/models";
import { getCurrentUser } from "../services/authService";
import { USER_TYPES } from "../utils/constants";

import useInternalProjects from "./useInternalProjects";
import useExternalProjects from "./useExternalProjects";

interface Params {
	searchParams: { 
		organisationId: string;
		facilityId: string;
		module: string;
		inspectionTypeId: string; 
		year: number;
		inspectableItems: string[];
	};
}

export interface InspectedCacheItem {
	projectDetails: Project;
	inspectedItems: InspectedItemDetails[];
}

const useGeneralReportOverviewItems = ({ searchParams }: Params) => {
	const { data: internalProjects, isFetching: isFetchingInternalProjects, dataUpdatedAt, refetch: refetchInternalProjects } = useInternalProjects();
	const { data: customerProjects, isFetching: isFetchingCustomerProjects, dataUpdatedAt: customerProjectsLastUpdated, refetch: refetchExternalProjects } = useExternalProjects();

	const user = getCurrentUser() as User;
	const isExternalUser = user.userType === USER_TYPES.EXTERNAL;

	useEffect(() => {
		if (isExternalUser) refetchExternalProjects();
		else refetchInternalProjects();
	}, [isExternalUser, refetchExternalProjects, refetchInternalProjects]);

	const projectDetails = useMemo(() => {
		if (isExternalUser) {
			return {
				allProjects: customerProjects.pages.flatMap((page) => page.projects),
				loading: isFetchingCustomerProjects,
				lastUpdated: customerProjectsLastUpdated,
			};
		}

		return {
			allProjects: internalProjects.pages.flatMap((page) => page.projects),
			loading: isFetchingInternalProjects,
			lastUpdated: dataUpdatedAt,
		};
	}, [customerProjects, isFetchingCustomerProjects, isFetchingInternalProjects, customerProjectsLastUpdated, internalProjects, dataUpdatedAt,  isExternalUser]);
	
	const inspectedItems: InspectedCacheItem[] = useMemo(() => {
		const mappedAssociatedProjects = projectDetails.allProjects.flatMap((project) => {
			const itemIDs = project.inspectedItemIDs ? project.inspectedItemIDs.map((itemID) => ({
				id: itemID,
				name: project.supervisorName, 
				date: project.report?.createdAt,
				isPublished: project.report?.isPublished
			})) : [];

			return {
				projectDetails: project,
				inspectedItems: _.uniqBy(itemIDs, (item) => item.id)
			};
		});

		return mappedAssociatedProjects;
	}, [projectDetails]);

	const overview = useMemo(() => {
		const totalItemsToInspect = searchParams.inspectableItems;
		const totalInspectedItems = _.uniqBy(inspectedItems.flatMap(item => item.inspectedItems), (item) => item.id);
        const totalUnispectedItems = _.difference(totalItemsToInspect, totalInspectedItems.map((item) => item.id));

        const publishedInspectionItems = totalInspectedItems.filter((item) => item.isPublished);
        const unpublishedInspectionItems = totalInspectedItems.filter((item) => !item.isPublished);

        return { 
			totalInspectedItems, 
			totalItemsToInspect, 
			totalUnispectedItems, 
			publishedInspectionItems, 
			unpublishedInspectionItems 
		};
    }, [inspectedItems, searchParams.inspectableItems]);

	console.log(projectDetails);
	return { isLoading: projectDetails.loading, inspectedItems, overview, lastUpdated: projectDetails.lastUpdated };
};


export default useGeneralReportOverviewItems;